import { getEncryptedField } from "core/model/encryption";
import type { FieldsHolderType } from "core/model/patients";
import {
  getAgeIntervalFromRangeStr,
  getWeightIntervalFromRangeStr,
  getWeightIntervalString,
} from "core/model/patients";
import { Auction } from "core/types";

export function handleAgeInterval(ageInterval: ToType) {
  if (typeof ageInterval === "string")
    return getAgeIntervalFromRangeStr(ageInterval);
  if (ageInterval?.value != null)
    return getAgeIntervalFromRangeStr(ageInterval.value);
  return ageInterval;
}

function normalizeSelect(specializations: ToType) {
  if (Array.isArray(specializations)) {
    return specializations.map((s: any) => (s.value ? s.value : s));
  }
  if (typeof specializations === "object")
    return specializations?.value
      ? Array.isArray(specializations.value)
        ? specializations.value.map((s: any) => s.value)
        : [specializations.value]
      : null;
  return null;
}

// injects values from form values into FieldsHolderType to allow for a diff check in the form updater
export function applyFormValueFields(
  fields: FieldsHolderType,
  value: AnyObject,
): FieldsHolderType {
  return {
    ...fields,
    solutions:
      value.solutions !== undefined ? value.solutions : fields.solutions,
    ...(value.start_date !== undefined && { start_date: value.start_date }),
    ...(value.birth_date !== undefined && {
      birth_date: getEncryptedField(value.birth_date),
    }),
    ...(value.age_interval !== undefined && {
      age_interval: value.age_interval,
    }),
    ...(value.specializations !== undefined && {
      specializations: normalizeSelect(value.specializations),
    }),
    ...(value.selected_payment_method !== undefined && {
      selected_payment_method: value.selected_payment_method,
      ...(value?.payers_insurance?.value !== null && {
        insurance: {
          id: value?.payers_insurance?.value,
          name: value?.payers_insurance?.label,
        },
      }),
      ...(value?.public_pension?.value !== null && {
        public_pension: {
          id: value?.public_pension?.value,
          name: value?.public_pension?.label,
        },
      }),
    }),
    ...(value.location?.zipcode !== undefined && {
      zipcode: value.location.zipcode,
    }),
    ...(value.location?.coordinates?.latitude !== undefined && {
      latitude: value.location.coordinates.latitude,
    }),
    ...(value.location?.coordinates?.longitude !== undefined && {
      longitude: value.location.coordinates.longitude,
    }),
    ...(value.radius_in_km && { radius_in_km: value.radius_in_km }),
    country_wide_search: value.country_wide_search,
    ...(value._palliative != null && {
      palliative: value._palliative,
      palliative_certificate:
        (value._palliative && value.palliative_certificate_needed) || false,
    }),
    ...(value.run_away_description !== undefined && {
      closed_area_needed: value.run_away_description != null,
    }),
    ...(value.protected_area_needed !== undefined && {
      protected_area_needed: value.protected_area_needed != null,
    }),
    ...(value._breathing != null && {
      oxygen_therapy: Boolean(value._breathing && value.oxygen_therapy != null),
      specialised_intensive_nurse: Boolean(
        value._breathing && value.specialised_intensive_nurse && true,
      ),
      tracheostomy: Boolean(value._breathing && value.tracheostomy != null),
    }),
    ...(value._base_care != null && { base_care: value._base_care }),
    ...(value._companionship != null && {
      companionship: value._companionship,
    }),
    ...(value._nutrition != null && { nutrition: value._nutrition }),
    ...(value._incontinence != null && { incontinence: value._incontinence }),
    ...(value._dialysis != null && { dialysis: value._dialysis }),
    ...(value._wound_care != null && { wound_care: value._wound_care }),
    ...(value._psychiatric_care != null && {
      psychiatric_care: value._psychiatric_care,
    }),
    ...(value._medication != null && { medication: value._medication }),
    ...(value._compression != null && { compression: value._compression }),
    ...(value._injection != null && { injection: value._injection }),
    ...(value.weight != null && {
      weight_interval: getWeightIntervalString(value.weight),
    }),
    ...(value._care_for_children_and_teenagers != null && {
      care_for_children_and_teenagers: value._care_for_children_and_teenagers,
    }),
    ...(value._care_for_youth != null && {
      care_for_youth: value._care_for_youth,
    }),
  };
}

// convert fields from the form into an auction to be able to check potential candidates
export function getAuctionFromFields(fields: FieldsHolderType): Auction {
  return {
    __typename: "Auction",
    country_wide_search: fields.country_wide_search,
    id: 1,
    patient: {
      care_needs: {
        base_care: fields.base_care ? {} : undefined,
        breathing:
          fields.tracheostomy ||
          fields.oxygen_therapy ||
          fields.specialised_intensive_nurse
            ? {
                oxygen_therapy: fields.oxygen_therapy ? "" : undefined,
                specialised_intensive_nurse: fields.specialised_intensive_nurse
                  ? true
                  : undefined,
                tracheostomy: fields.tracheostomy ? "" : undefined,
              }
            : undefined,
        care_for_children_and_teenagers: fields.care_for_children_and_teenagers
          ? {}
          : undefined,
        care_for_youth: fields.care_for_youth ? {} : undefined,
        companionship: fields.companionship ? {} : undefined,
        compression: fields.compression ? {} : undefined,
        dialysis: fields.dialysis ? {} : undefined,
        incontinence: fields.incontinence ? {} : undefined,
        injection: fields.injection ? {} : undefined,
        medication: fields.medication ? {} : undefined,
        nutrition: fields.nutrition ? {} : undefined,
        palliative: fields.palliative
          ? Object.assign(
              {
                description: "",
              },
              fields.palliative_certificate
                ? {
                    palliative_certificate_needed: true,
                  }
                : undefined,
            )
          : undefined,
        psychiatric_care: fields.psychiatric_care ? {} : undefined,
        wound_care: fields.wound_care ? {} : undefined,
      },
      careseeker: fields.careseeker_id
        ? { __typename: "Careseeker", id: fields.careseeker_id }
        : undefined,
      diagnosis: {
        mental_health:
          fields.closed_area_needed || fields.protected_area_needed
            ? {
                protected_area_needed: fields.protected_area_needed
                  ? ""
                  : undefined,
                run_away_description: fields.closed_area_needed
                  ? ""
                  : undefined,
              }
            : undefined,
      },
      id: 1,
      profile: {
        __typename: "PatientProfile",
        age_interval: handleAgeInterval(fields.age_interval),
        care_duration_in_days: fields.care_duration_in_days,
        weight_interval: getWeightIntervalFromRangeStr(fields.weight_interval),
      },
      version: "2",
    },
    payers: {
      insurance: fields.insurance,
      public_pension: fields.public_pension || undefined,
      selected_payment_method: fields.selected_payment_method || undefined,
    },
    profile: {
      search_location: {
        latitude: fields.latitude,
        longitude: fields.longitude,
        zipcode: fields.zipcode,
      },
    },
    radius_in_meter: fields.radius_in_km
      ? fields.radius_in_km * 1000
      : undefined,
    search_type: fields.search_type,
    solutions: fields.solutions,
    specializations: normalizeSelect(fields.specializations),
    start_date: fields.start_date,
    start_date_flexible: fields.start_date_flexible,
  };
}
